import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';
import type { AnalysisChoice, LinkedTicket, PlanLine } from '../api/types.js';

/** The team's sprint on the Jira boards the server follows (ADR-034); read again every 10 minutes while open */
export function useSprint(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['sprint', serverId], queryFn: () => api.sprint(serverId), refetchInterval: 10 * 60_000, staleTime: 60_000 });
}

/** Ask for a summary (ADR-034 section 2): the computed sprint goes to the board's sprint session */
export function useSprintSummary(serverId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (boardId: number) => api.sprintSummary(serverId, boardId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sprint', serverId] }),
  });
}

/** Another sprint of a board, closed or coming (null: the board's default view), read when chosen */
export function useSprintById(serverId: string, boardId: number, sprintId: number | null) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['sprint', serverId, boardId, sprintId],
    queryFn: () => api.sprintById(serverId, boardId, sprintId!),
    enabled: sprintId !== null,
    staleTime: 5 * 60_000,
  });
}

/** The analyses of the tickets that came into the boards (ADR-034 step 3a); read again every 2 minutes while open */
export function useSprintAnalyses(serverId: string) {
  const api = useApiClient();
  // Every 15 seconds while a ticket's session is being prepared for a discussion, to open it as soon as it is ready
  return useQuery({
    queryKey: ['sprint-analyses', serverId], queryFn: () => api.sprintAnalyses(serverId),
    refetchInterval: (q) => (q.state.data?.analyses.some((a) => a.discuss?.status === 'queued') ? 15_000 : 2 * 60_000),
  });
}

/** Apply, Ask the author, Dismiss: each one reads the analyses again when done */
export function useAnalysisDecisions(serverId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const onSettled = () => queryClient.invalidateQueries({ queryKey: ['sprint-analyses', serverId] });
  return {
    apply: useMutation({ mutationFn: ({ key, choice }: { key: string; choice: AnalysisChoice }) => api.applyAnalysis(serverId, key, choice), onSettled }),
    ask: useMutation({ mutationFn: ({ key, comment }: { key: string; comment: string }) => api.askAuthor(serverId, key, comment), onSettled }),
    dismiss: useMutation({ mutationFn: (key: string) => api.dismissAnalysis(serverId, key), onSettled }),
    analyse: useMutation({ mutationFn: ({ key, boardId }: { key: string; boardId: number }) => api.analyseTicket(serverId, key, boardId), onSettled }),
    deep: useMutation({ mutationFn: (key: string) => api.deepAnalysis(serverId, key), onSettled }),
    discuss: useMutation({ mutationFn: (key: string) => api.discussAnalysis(serverId, key), onSettled }),
    implement: useMutation({ mutationFn: ({ key, repository, approach }: { key: string; repository: string; approach: string }) => api.implementJira(serverId, key, { repository, approach }), onSettled: () => { onSettled(); void queryClient.invalidateQueries({ queryKey: ['tickets', serverId] }); } }),
    linked: useMutation({ mutationFn: ({ key, ticket }: { key: string; ticket: LinkedTicket }) => api.createLinked(serverId, key, ticket), onSettled }),
    postPlan: useMutation({ mutationFn: ({ key, comment }: { key: string; comment: string }) => api.postPlan(serverId, key, comment), onSettled }),
  };
}

/** The board's next sprint plan (ADR-034 step 4); read again every 30 seconds while it is being prepared or applied */
export function useSprintPlan(serverId: string, boardId: number, enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['sprint-plan', serverId, boardId],
    queryFn: () => api.sprintPlan(serverId, boardId),
    enabled,
    refetchInterval: (q) => (['queued', 'applying'].includes(q.state.data?.plan?.status ?? '') ? 30_000 : false),
  });
}

/** Prepare, Apply the plan, Dismiss */
export function usePlanDecisions(serverId: string, boardId: number) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const onSettled = () => {
    void queryClient.invalidateQueries({ queryKey: ['sprint-plan', serverId, boardId] });
    void queryClient.invalidateQueries({ queryKey: ['sprint', serverId] });
  };
  return {
    prepare: useMutation({ mutationFn: (away: string[]) => api.preparePlan(serverId, boardId, away), onSettled }),
    apply: useMutation({ mutationFn: (lines: PlanLine[]) => api.applyPlan(serverId, boardId, lines), onSettled }),
    dismiss: useMutation({ mutationFn: () => api.dismissPlan(serverId, boardId), onSettled }),
  };
}
